import React, { useState, useEffect } from 'react';
import { useAppDispatch } from '../../redux/store';
import { useSelector } from 'react-redux';
import { selectConfig } from '../../redux/slices/configSlice';
import { selectPlayers } from '../../redux/slices/playersSlice';
import { signInTwilio } from '../../redux/slices/userSlice';
import { setDafaplusPreChatStep, setPage } from '../../redux/slices/pageSlice';
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { getUrlParam, capitalizeWords, getUrlElement, decodeEntities } from '../../utils/common';
import { changeEngagementPhase } from '../../twilio/store/genericActions';
import { EngagementPhase } from '../../twilio/store/definitions';
import { initSession } from '../../twilio/store/initSession';
import { sessionDataHandler } from '../../sessionDataHandler';
import usePreChatDafaplusStyles from './style';
import GetAppIcon from '@mui/icons-material/GetApp';
import PublishIcon from '@mui/icons-material/Publish';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PublicIcon from '@mui/icons-material/Public';
import { DAFAPLUS_FAQS } from './dafaplusFaqs';
import {
  DAFAPLUS_LANGUAGE_STORAGE_KEY,
  getDafaplusLanguageText,
} from './dafaplusLanguage';

interface User {
  brand: string;
  level: string;
  lang: string;
  country: string;
  product: string;
  inquiry: string;
  source: string;
  payment: string;
  opm: string;
  init_msg?: string;
  referer: string;
  refererPlatform: string;
  cat: string;
}

const useIpAndCountry = () => {
  const [ipAddress, setIpAddress] = useState('');
  const [countryCode, setCountryCode] = useState('');

  useEffect(() => {
    const fetchIpAndCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();

        if (data.ip && data.country) {
          setIpAddress(data.ip);
          setCountryCode(data.country_code);
          return;
        }

        const cfResponse = await fetch('https://cloudflare.com/cdn-cgi/trace');
        const cfText = await cfResponse.text();

        if (cfText) {
          const dataObj: Record<string, string> = cfText.trim().split('\n').reduce((obj, pair) => {
            const parts = pair.split('=');
            if (parts.length === 2) {
              const [key, value] = parts;
              return { ...obj, [key]: value };
            }
            return obj;
          }, {} as Record<string, string>);

          if (dataObj.ip) {
            setIpAddress(dataObj.ip);
            setCountryCode(dataObj.loc || '');
            return;
          }
        }

        const geoResponse = await fetch('https://geolocation-db.com/json/');
        const geoData = await geoResponse.json();

        if (geoData.IPv4) {
          setIpAddress(geoData.IPv4);
          setCountryCode(geoData.country_code);
        }
      } catch (error) {
        console.error('Error fetching IP and Country Code:', error);
      }
    };

    fetchIpAndCountry();
  }, []);

  return { ipAddress, countryCode };
};

const LANGUAGE_OPTIONS = [
  { value: 'IN', label: 'English', flagCode: 'in' },
  { value: 'MY', label: 'English', flagCode: 'my' },
  { value: 'EN', label: 'English', flagCode: 'globe' },
  { value: 'TH', label: 'ภาษาไทย', flagCode: 'th' },
  { value: 'KR', label: '한국어', flagCode: 'kr' },
];

const FlagImg: React.FC<{ code: string; label: string }> = ({ code, label }) => {
  if (code === 'globe') {
    return (
      <PublicIcon
        titleAccess={label}
        style={{ width: 24, height: 18, verticalAlign: 'middle', marginRight: 6 }}
      />
    );
  }

  return (
    <img
      src={`https://flagcdn.com/24x18/${code}.png`}
      srcSet={`https://flagcdn.com/48x36/${code}.png 2x`}
      width="24"
      height="18"
      alt={label}
      style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 6, borderRadius: 2 }}
    />
  );
};

const getLanguageOption = (value: string) =>
  LANGUAGE_OPTIONS.find((option) => option.value === value);

const persistDafaplusLanguage = (language: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.setItem(DAFAPLUS_LANGUAGE_STORAGE_KEY, language);
};

const DAFAPLUS_PRECHAT_STEP_STORAGE_KEY = 'dafaplusPreChatStep';

const getStoredDafaplusPreChatStep = (): 'faq' | 'category' => {
  if (typeof window === 'undefined') {
    return 'faq';
  }

  return localStorage.getItem(DAFAPLUS_PRECHAT_STEP_STORAGE_KEY) === 'category' ? 'category' : 'faq';
};

const LANGUAGE_UI_TEXT: Record<string, {
  pickQuery: string;
  deposit: string;
  withdrawal: string;
  bet: string;
  bonus: string;
  other: string;
}> = {
  EN: {
    pickQuery: 'Pick a query to start the chat',
    deposit: 'Deposit',
    withdrawal: 'Withdrawal',
    bet: 'Bet',
    bonus: 'Bonus',
    other: 'Other',
  },
  IN: {
    pickQuery: 'Pick a query to start the chat',
    deposit: 'Deposit',
    withdrawal: 'Withdrawal',
    bet: 'Bet',
    bonus: 'Bonus',
    other: 'Other',
  },
  MY: {
    pickQuery: 'Pick a query to start the chat',
    deposit: 'Deposit',
    withdrawal: 'Withdrawal',
    bet: 'Bet',
    bonus: 'Bonus',
    other: 'Other',
  },
  KR: {
    pickQuery: '채팅을 시작하려면 골라주세요',
    deposit: '입금',
    withdrawal: '출금',
    bet: '배팅',
    bonus: '보너스',
    other: '기타',
  },
  TH: {
    pickQuery: 'เลือกหัวข้อเพื่อเริ่มการสนทนา',
    deposit: 'การฝากเงิน',
    withdrawal: 'การถอนเงิน',
    bet: 'การเดิมพัน',
    bonus: 'โบนัส',
    other: 'อื่นๆ',
  },
};

const PreChatDafaplus: React.FC = () => {
  const classes = usePreChatDafaplusStyles();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFaqId, setExpandedFaqId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedLanguage, setSelectedLanguage] = useState(() => (
    typeof window === 'undefined' ? '' : (localStorage.getItem(DAFAPLUS_LANGUAGE_STORAGE_KEY) || '')
  ));
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const { ipAddress, countryCode } = useIpAndCountry();

  const dispatch = useAppDispatch();
  const config = useSelector(selectConfig);
  const players = useSelector(selectPlayers);
  const currentStep = useSelector((state: any) => state.page.dafaplusPreChatStep as 'faq' | 'category');
  const playerData = players?.data ? players?.data.attributes : null;

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredFAQs = DAFAPLUS_FAQS.filter((faq) => {
    if (!normalizedSearchTerm) {
      return true;
    }

    const answerText = decodeEntities(faq.answerHtml).toLowerCase();

    return (
      faq.title.toLowerCase().includes(normalizedSearchTerm) ||
      answerText.includes(normalizedSearchTerm)
    );
  });
  const displayedFAQs = normalizedSearchTerm ? filteredFAQs : filteredFAQs.slice(0, 3);
  const selectedLanguageCode = selectedLanguage || 'EN';
  const uiText = LANGUAGE_UI_TEXT[selectedLanguageCode] || LANGUAGE_UI_TEXT.EN;

  const generateUserLevel = (): string => {
    let level = 'REG';
    const levelInt = getUrlParam('level');

    if (levelInt) {
      if (['17', '26'].includes(levelInt)) {
        level = 'VIP';
      } else if (['13', '18'].includes(levelInt)) {
        level = 'Elite';
      }
    }
    return level;
  };

  const appGenerateSkillset = (user: User): string => {
    let cat = user.cat;
    cat = capitalizeWords(cat);
    if (cat === 'Dafabet') {
      cat = 'Dafa';
    }

    const lang = user.lang || 'EN';
    const options = ['WC', lang, cat, '__REG_PI'];

    return options.join('_');
  };

  const getReferer = (defaultValue = ''): string => {
    let referer = document.referrer;

    if (!referer || referer === '') {
      const urlParams = new URLSearchParams(window.location.search);
      referer = urlParams.get('referer') || defaultValue;
    }

    try {
      const url = new URL(referer);
      return `${url.protocol}//${url.host}${url.pathname}`;
    } catch (error) {
      return defaultValue;
    }
  };

  useEffect(() => {
    dispatch(setDafaplusPreChatStep(getStoredDafaplusPreChatStep()));
  }, [dispatch]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.setItem(DAFAPLUS_PRECHAT_STEP_STORAGE_KEY, currentStep);
  }, [currentStep]);

  // Reflect selections made in another tab of the same browser live (e.g. picking a
  // language on the category page updates every other open tab's select box too).
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key === DAFAPLUS_LANGUAGE_STORAGE_KEY) {
        setSelectedLanguage(event.newValue || '');
      } else if (event.key === DAFAPLUS_PRECHAT_STEP_STORAGE_KEY) {
        dispatch(setDafaplusPreChatStep(event.newValue === 'category' ? 'category' : 'faq'));
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [dispatch]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const handleStartChat = () => {
    dispatch(setDafaplusPreChatStep('category'));
  };

  const handleCategorySelect = async (category: string) => {
    if (!selectedLanguage) {
      return;
    }

    setSelectedCategory(category);
    setSubmitDisabled(true);

    try {
      const formData = config?.data?.[0]?.attributes;
      let lang = 'EN';
      let locale = config?.data?.[0]?.attributes?.locale || 'EN';

      const langMap: Record<string, string> = {
        'en-IN': 'in',
        'kk': 'kz',
        'zh-Hans': 'cn',
        'ko-KR': 'kr',
        'vi': 'vn',
        'ne': 'np',
        'zh-Hant': 'cn',
        'bn-BD': 'bd',
        'en-PK': 'pk',
      };

      lang = (selectedLanguage || (langMap[locale] ?? locale)).toUpperCase();

      let user: User = {
        brand: getUrlParam('brand') || 'Dafa',
        level: generateUserLevel(),
        lang: lang || 'EN',
        country: config.countryCode || countryCode || '',
        product: getUrlParam('product') || '',
        inquiry: category !== 'Other' ? category : getUrlParam('inquiry') || '',
        source: 'EWC',
        payment: getUrlParam('payment') || '',
        opm: getUrlParam('otherpayment') || 'NULL',
        init_msg: config.init_msg,
        referer: config.source || '',
        refererPlatform: navigator.userAgent,
        cat: '',
      };

      const brandMapping: Record<string, string> = {
        dfbt: 'dafabet',
        sevenbaby: '777baby',
      };

      let brand = getUrlElement(7);

      if (brand !== 'df888' && brand !== 'df888-gold') {
        brand = brandMapping[formData?.Template?.Brand || ''] || formData?.Template?.Brand || brand;
      }

      let product = user.product.toLowerCase();

      if (brand === 'zedbet' && (lang === 'EN' || lang === 'KR')) {
        user.inquiry = 'GI';
      }

      if (product === 'dafabet') {
        product = 'Dafabet';
      }

      const lastPathSegment = window.location.pathname.split('/').pop();
      if (lastPathSegment === 'dafa-spain') {
        brand = 'dafabetes';
      }

      user.cat = brand || 'Dafa';

      if (
        window.location.hostname === 'istg-chat.elysium-dfbt.com' ||
        window.location.hostname === 'localhost'
      ) {
        brand = `istg-${brand}`;
        lang = `ISTG ${lang}`;
      }

      const skillset = appGenerateSkillset(user);
      const normalizedUsername = playerData?.username?.trim().toLowerCase() || 'Guest';

      const customValues = [
        { title: 'IP', value: ipAddress || '202.151.35.161' },
        { title: 'country', value: countryCode || '' },
        { title: 'referer', value: getReferer() || '' },
        { title: 'RefererPlatform', value: navigator.userAgent },
        { title: 'CustomerVerified', value: playerData ? 'true' : 'false' },
        { title: 'language', value: (lang || 'EN').toUpperCase() },
        { title: 'CustomerSegment', value: user.level },
        { title: 'SuggestedPM', value: user.payment },
        { title: 'OtherPM', value: user.opm },
        { title: 'Restart', value: 'false' },
        { title: 'skillset', value: skillset },
        { title: 'username', value: normalizedUsername },
        { title: 'name', value: normalizedUsername },
        { title: 'email', value: playerData?.email || '' },
        { title: 'phoneNumber', value: '' },
        { title: 'product', value: product },
        { title: 'inquiry', value: category !== 'Other' ? category : '' },
        { title: 'subinquiry', value: '' },
        { title: 'deposit', value: '' },
        { title: 'category', value: category }, // Add category
      ];

      if (product) {
        customValues.push({ title: 'Product', value: product });
      }

      let engagementParameters: Record<string, any> = {
        Brand: brand,
      };

      customValues.forEach(item => {
        engagementParameters[item.title] = item.value;
      });

      const languageText = getDafaplusLanguageText(selectedLanguage);
      const startHelloMessage = languageText.chatWillStartNow;

      const twilioFormData = {
        friendlyName: normalizedUsername || 'Customer',
        email: playerData?.email || undefined,
        query: startHelloMessage || (formData?.ChatBox?.HelloMessageText ?? 'Hello'),
        ...engagementParameters
      };

      dispatch(changeEngagementPhase({ phase: EngagementPhase.Loading }));
      dispatch(signInTwilio({ formData: twilioFormData }))
        .then((result: any) => {
          if (result.meta.requestStatus === 'fulfilled') {
            const { token, conversationSid, expiration, engagementParameters: sessionEngagementParameters } = result.payload.userData || {};
            if (token && conversationSid) {
              sessionDataHandler.storeSession({ token, conversationSid, expiration, engagementParameters: sessionEngagementParameters });
              dispatch(initSession({ token, conversationSid }) as any);
            }
            dispatch(setDafaplusPreChatStep('faq'));
            dispatch(setPage('chat'));
          } else {
            setSubmitDisabled(false);
            dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
          }
        })
        .catch((error: any) => {
          console.error('Error initiating chat:', error);
          setSubmitDisabled(false);
          dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
        });
    } catch (error) {
      console.error('Error in handleCategorySelect:', error);
      setSubmitDisabled(false);
    }
  };

  if (currentStep === 'faq') {
    return (
      <Box className={classes.root}>
        <Box className={classes.pageBody}>
          <Box className={classes.hero}>
            <Typography className={classes.heroQuestion}>
              How can we help you?
            </Typography>
          </Box>

          <Box className={classes.searchWrap}>
            <TextField
              fullWidth
              placeholder="Search for help"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={classes.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box className={classes.content}>
            <Box className={classes.faqList}>
              {displayedFAQs.length > 0 ? (
                displayedFAQs.map((faq) => (
                  <Box
                    key={faq.id}
                    className={classes.faqCard}
                    onClick={() => setExpandedFaqId((prev) => (prev === faq.id ? null : faq.id))}
                  >
                    <Typography className={classes.faqTitle}>
                      {faq.title}
                    </Typography>
                    <Typography
                      className={`${classes.faqDescription} ${
                        expandedFaqId === faq.id ? classes.faqDescriptionExpanded : ''
                      }`}
                    >
                      {decodeEntities(faq.answerHtml).replace(/\s+/g, ' ').trim()}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography className={classes.emptyState}>
                  No FAQs found
                </Typography>
              )}
            </Box>

            <Button
              fullWidth
              variant="contained"
              onClick={handleStartChat}
              className={`${classes.primaryCta} ${classes.faqPrimaryCta}`}
            >
              Start Chat
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  // Category Selection Page
  return (
    <Box className={classes.root}>
      <Box className={classes.pageBody}>
        <Box className={`${classes.hero}`}>
          <Typography className={classes.heroQuestion}>
            {uiText.pickQuery}
          </Typography>
        </Box>

        <Box className={classes.categoryContent} >
          <FormControl fullWidth required className={classes.languageSelectorWrap} error={!selectedLanguage}>
            <Select
              value={selectedLanguage}
              inputProps={{ 'aria-label': 'Select Language' }}
              onChange={(event) => {
                const language = String(event.target.value);
                setSelectedLanguage(language);
                persistDafaplusLanguage(language);
              }}
              className={classes.languageSelector}
              displayEmpty
              renderValue={(value) => {
                if (!value) {
                  return (
                    <Box className={classes.languageOptionItem}>
                      <span className={classes.languageOptionTextMuted}>Select Language</span>
                    </Box>
                  );
                }

                const selectedOption = getLanguageOption(String(value));

                if (!selectedOption) {
                  return <span className={classes.languageOptionTextMuted}>Select Language</span>;
                }

                return (
                  <Box className={classes.languageOptionItem}>
                    <FlagImg code={selectedOption.flagCode} label={selectedOption.label} />
                    <span>{selectedOption.label}</span>
                  </Box>
                );
              }}
              MenuProps={{
                classes: { paper: classes.languageMenuPaper },
                MenuListProps: { className: classes.languageMenuList },
              }}
            >
              {LANGUAGE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value} className={classes.languageMenuItem}>
                  <Box className={classes.languageOptionItem}>
                    <FlagImg code={option.flagCode} label={option.label} />
                    <span>{option.label}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {!selectedLanguage && (
              <FormHelperText>Please select a language to continue</FormHelperText>
            )}
          </FormControl>

          <Box className={classes.categoryGrid}>
            <Button
              fullWidth
              onClick={() => handleCategorySelect('DP')}
              disabled={submitDisabled || !selectedLanguage}
              className={`${classes.categoryButton} ${classes.depositButton}`}
            >
              <GetAppIcon/>
              {uiText.deposit}
            </Button>

            <Button
              fullWidth
              onClick={() => handleCategorySelect('WD')}
              disabled={submitDisabled || !selectedLanguage}
              className={`${classes.categoryButton} ${classes.withdrawalButton}`}
            >
              <PublishIcon/>
              {uiText.withdrawal}
            </Button>

            <Button
              fullWidth
              onClick={() => handleCategorySelect('BT')}
              disabled={submitDisabled || !selectedLanguage}
              className={`${classes.categoryButton} ${classes.betButton}`}
            >
              <CurrencyBitcoinIcon/>
              {uiText.bet}
            </Button>

            <Button
              fullWidth
              onClick={() => handleCategorySelect('BO')}
              disabled={submitDisabled || !selectedLanguage}
              className={`${classes.categoryButton} ${classes.bonusButton}`}
            >
             <CardGiftcardIcon/>
              {uiText.bonus}
            </Button>
          </Box>

          <Box className={classes.bottomAction}>
            <Button
              fullWidth
              onClick={() => handleCategorySelect('Other')}
              disabled={submitDisabled || !selectedLanguage}
              className={`${classes.primaryCta} ${classes.faqPrimaryCta}`}
            >
              {submitDisabled ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                  Loading...
                </>
              ) : (
                uiText.other
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PreChatDafaplus;
